import { inject, Injectable, PLATFORM_ID, signal, computed } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationStart } from '@angular/router';
import { AuthService } from './auth.service';
import { EncryptionService } from './encryption.service';
import { User, InfographicsViews } from '../models/auth';

@Injectable({
  providedIn: 'root',
})
export class InfographicsService {
  private authService = inject(AuthService);
  private encryptionService = inject(EncryptionService);
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);

  readonly activeInfographic = signal<
    'immediate_or_schedule' | 'transport_schedule' | 'rental_schedule' | 'delivery_immediate' | null
  >(null);

  readonly showAvailableVehicleInfo = computed(
    () =>
      this.activeInfographic() === 'transport_schedule' ||
      this.activeInfographic() === 'rental_schedule',
  );
  readonly showTransportInqInfo = computed(
    () => this.activeInfographic() === 'immediate_or_schedule',
  );
  readonly showPostEnqInfographics = computed(
    () => this.activeInfographic() === 'delivery_immediate',
  );

  private shownTimestamp = 0;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      // Global listener to dismiss infographic on click
      document.addEventListener(
        'click',
        event => {
          const active = this.activeInfographic();
          if (active) {
            // Only dismiss if at least 300ms has passed since showing to avoid immediate click dismissal
            if (Date.now() - this.shownTimestamp > 300) {
              event.preventDefault();
              event.stopPropagation();
              this.dismissInfographic(active);
            }
          }
        },
        { capture: true },
      );

      // Global router navigation listener to clean up the infographic overlay when navigating away (including back button)
      this.router.events.subscribe(event => {
        if (event instanceof NavigationStart) {
          this.activeInfographic.set(null);
        }
      });
    }
  }

  checkAndIncrement(
    key: 'immediate_or_schedule' | 'transport_schedule' | 'rental_schedule' | 'delivery_immediate',
  ): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const profile = this.encryptionService.decryptLocalStorage('profile');
    if (!profile || !profile.infographics_views || profile.infographics_views[key] === undefined)
      return;

    const currentCount = profile.infographics_views[key] ?? 0;

    // Show infographic overlay if the user has viewed and dismissed it less than 3 times
    if (currentCount < 3) {
      this.activeInfographic.set(key);
      this.shownTimestamp = Date.now();
    }
  }

  /**
   * Call this from the component's ngOnDestroy.
   * Clears the active infographic state so it doesn't persist on other pages if navigated away.
   */
  clearVisit(
    key: 'immediate_or_schedule' | 'transport_schedule' | 'rental_schedule' | 'delivery_immediate',
  ): void {
    if (this.activeInfographic() === key) {
      this.activeInfographic.set(null);
    }
  }

  dismissInfographic(
    key: 'immediate_or_schedule' | 'transport_schedule' | 'rental_schedule' | 'delivery_immediate',
  ): void {
    this.activeInfographic.set(null);

    const profile = this.encryptionService.decryptLocalStorage('profile');
    if (!profile || !profile.infographics_views || profile.infographics_views[key] === undefined)
      return;

    const currentCount = profile.infographics_views[key] ?? 0;

    // Increment count and update profile (both locally and via the API) only if it is less than 3
    if (currentCount < 3) {
      const newCount = currentCount + 1;
      this.updateProfileLocally(key, newCount);
      this.updateViewsOnApi(key, newCount, null);
    }
  }

  private updateProfileLocally(
    key: 'immediate_or_schedule' | 'transport_schedule' | 'rental_schedule' | 'delivery_immediate',
    newValue: number,
  ): void {
    const currentProfile = this.encryptionService.decryptLocalStorage('profile');
    if (currentProfile) {
      const currentViews = currentProfile.infographics_views || {};
      const updatedViews = { ...currentViews, [key]: newValue };

      const updatedProfile = {
        ...currentProfile,
        infographics_views: updatedViews,
      };
      // Preserve authentication token
      updatedProfile.token = currentProfile.token;
      this.encryptionService.encryptLocalStorage(updatedProfile, 'profile');
      this.authService.profileDetails.set(updatedProfile);
    }
  }

  private updateViewsOnApi(
    key: 'immediate_or_schedule' | 'transport_schedule' | 'rental_schedule' | 'delivery_immediate',
    newValue: number,
    keyToShow:
      | 'immediate_or_schedule'
      | 'transport_schedule'
      | 'rental_schedule'
      | 'delivery_immediate'
      | null,
  ): void {
    const payload = {
      infographics_views: {
        [key]: newValue,
      } as any,
    };
    this.authService.updateProfile(payload).subscribe({
      next: (res: User) => {
        const currentProfile = this.encryptionService.decryptLocalStorage('profile');
        if (currentProfile) {
          const currentViews = currentProfile.infographics_views || {};
          const updatedViews = { ...currentViews, [key]: newValue };

          const updatedProfile = {
            ...currentProfile,
            ...res,
            infographics_views: updatedViews,
          };
          // Preserve authentication token
          updatedProfile.token = currentProfile.token;
          this.encryptionService.encryptLocalStorage(updatedProfile, 'profile');
          this.authService.profileDetails.set(updatedProfile);
        }

        if (keyToShow) {
          this.activeInfographic.set(keyToShow);
          this.shownTimestamp = Date.now();
        }
      },
      error: err => {
        console.error('Failed to update infographics views count:', err);
      },
    });
  }
}
